import React, { useState, useEffect, useCallback } from 'react';
import { Card, Button, Badge, Input, Modal } from '../../../components/ui';
import { Search, Download, Eye, RefreshCw, ChevronLeft, ChevronRight } from 'lucide-react';
import { fetchAPI, API_BASE_URL, STORAGE_TOKEN_KEY } from '../../../services/apiClient';

// ---------------------------------------------------------------------------
// Tipos
// ---------------------------------------------------------------------------
interface AuditoriaLog {
  id: number;
  banca_id: string | null;
  usuario_id: string;
  usuario_nombre?: string | null;
  usuario_username?: string | null;
  rol?: string | null;
  local_nombre?: string | null;
  rol_usuario: string;
  accion: string;
  modulo: string;
  entidad_id: string | null;
  descripcion: string;
  datos_antes: string | null;
  datos_despues: string | null;
  ip: string | null;
  created_at: string;
}

interface AuditoriaListaResponse {
  total: number;
  pagina: number;
  por_pagina: number;
  items: AuditoriaLog[];
}

interface Filtros {
  fecha_inicio: string;
  fecha_fin: string;
  modulo: string;
  accion: string;
  usuario_id: string;
}

// ---------------------------------------------------------------------------
// Helpers de presentación
// ---------------------------------------------------------------------------
const ETIQUETAS_ACCION: Record<string, { label: string; color: string }> = {
  CREATE: { label: 'Creación', color: 'bg-green-100 text-green-800' },
  UPDATE: { label: 'Edición', color: 'bg-blue-100 text-blue-800' },
  DELETE: { label: 'Eliminación', color: 'bg-red-100 text-red-800' },
  LOGIN:  { label: 'Login', color: 'bg-purple-100 text-purple-800' },
  CIERRE: { label: 'Cierre', color: 'bg-orange-100 text-orange-800' },
};

const ETIQUETAS_MODULO: Record<string, string> = {
  sorteos:      '🎰 Sorteos',
  tickets:      '🎫 Tickets',
  restringidos: '🚫 Restringidos',
  locales:      '🏪 Locales',
  vendedores:   '👤 Vendedores',
  auth:         '🔐 Auth',
  premios:      '💸 Premios',
  listas:       '📄 Listas',
};

function formatFecha(iso: string): string {
  try {
    return new Date(iso).toLocaleString('es-CR', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function AccionBadge({ accion }: { accion: string }) {
  const info = ETIQUETAS_ACCION[accion] ?? { label: accion, color: 'bg-gray-100 text-gray-700' };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${info.color}`}>
      {info.label}
    </span>
  );
}

function JsonViewer({ titulo, json }: { titulo: string; json: string | null }) {
  if (!json) return <p className="text-gray-400 text-sm italic">Sin datos</p>;
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch {
    parsed = json;
  }
  return (
    <div>
      <p className="text-xs font-semibold text-gray-500 mb-1">{titulo}</p>
      <pre className="bg-gray-50 rounded p-3 text-xs overflow-auto max-h-48 text-gray-800 whitespace-pre-wrap break-words">
        {JSON.stringify(parsed, null, 2)}
      </pre>
    </div>
  );
}

function formatColones(value: number): string {
  if (!Number.isFinite(value)) return '₡0';
  return `₡${Math.round(value).toLocaleString('es-CR')}`;
}

interface TicketJugadaAuditoria {
  numero: string;
  monto: number;
}

interface TicketAuditoriaLegible {
  ticketNumber: string;
  sorteo: string;
  total: number;
  jugadas: TicketJugadaAuditoria[];
}

function tryParseJson(value: string | null): any {
  if (!value) return null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function extraerTicketLegible(log: AuditoriaLog): TicketAuditoriaLegible | null {
  if (log.modulo !== 'tickets') return null;
  const parsed = tryParseJson(log.datos_despues);
  if (!parsed || typeof parsed !== 'object') return null;

  const ticketNumber = String(parsed.ticket_number || '').trim();
  const sorteo = String(parsed.sorteo || '').trim();
  const totalNum = Number(parsed.total);
  const jugadasRaw = Array.isArray(parsed.jugadas) ? parsed.jugadas : [];

  const jugadas = jugadasRaw
    .map((j: any) => ({
      numero: String(j?.numero ?? '').trim(),
      monto: Number(j?.monto),
    }))
    .filter((j: TicketJugadaAuditoria) => j.numero && Number.isFinite(j.monto))
    .sort((a: TicketJugadaAuditoria, b: TicketJugadaAuditoria) =>
      a.numero.localeCompare(b.numero, 'es', { numeric: true }),
    )
    .slice(0, 50);

  if (!ticketNumber || !sorteo || !Number.isFinite(totalNum) || jugadas.length === 0) {
    return null;
  }

  return {
    ticketNumber,
    sorteo,
    total: totalNum,
    jugadas,
  };
}

function TicketDetalleNegocio({ data }: { data: TicketAuditoriaLegible }) {
  return (
    <div className="rounded-lg border border-blue-100 bg-blue-50/40 p-4 space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-semibold text-gray-800">Ticket: {data.ticketNumber}</span>
        <Badge variant="primary">Sorteo: {data.sorteo}</Badge>
      </div>

      <div>
        <p className="text-xs font-semibold text-gray-500 mb-1">Jugadas</p>
        <div className="space-y-1 max-h-64 overflow-auto pr-1">
          {data.jugadas.map((j, idx) => (
            <div key={`${j.numero}-${idx}`} className="flex items-center justify-between text-sm border-b border-blue-100 pb-1">
              <span className="font-mono text-gray-700">{j.numero}</span>
              <span className="font-semibold text-gray-900">{formatColones(j.monto)}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="pt-2 border-t border-blue-100 flex items-center justify-between">
        <span className="text-sm font-semibold text-gray-600">Total</span>
        <span className="text-base font-bold text-blue-700">{formatColones(data.total)}</span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Componente principal
// ---------------------------------------------------------------------------
export const AuditoriaPage: React.FC = () => {
  const [registros, setRegistros] = useState<AuditoriaLog[]>([]);
  const [total, setTotal] = useState(0);
  const [pagina, setPagina] = useState(1);
  const POR_PAGINA = 50;

  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [filtros, setFiltros] = useState<Filtros>({
    fecha_inicio: '',
    fecha_fin: '',
    modulo: '',
    accion: '',
    usuario_id: '',
  });

  const [logSeleccionado, setLogSeleccionado] = useState<AuditoriaLog | null>(null);

  // ---------------------------------------------------------------------------
  // Carga de datos
  // ---------------------------------------------------------------------------
  const cargar = useCallback(async (pg: number, f: Filtros) => {
    setCargando(true);
    setError(null);
    try {
      const params = new URLSearchParams({ pagina: String(pg), por_pagina: String(POR_PAGINA) });
      if (f.fecha_inicio) params.append('fecha_inicio', f.fecha_inicio);
      if (f.fecha_fin) params.append('fecha_fin', f.fecha_fin);
      if (f.modulo) params.append('modulo', f.modulo);
      if (f.accion) params.append('accion', f.accion);
      if (f.usuario_id) params.append('usuario_id', f.usuario_id);

      const data = await fetchAPI<AuditoriaListaResponse>(`/auditoria?${params}`);
      setRegistros(data.items);
      setTotal(data.total);
    } catch (e: any) {
      setError(e.message || 'Error al cargar auditoría');
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    cargar(pagina, filtros);
  }, [pagina, cargar]); // eslint-disable-line react-hooks/exhaustive-deps

  const aplicarFiltros = () => {
    setPagina(1);
    cargar(1, filtros);
  };

  const limpiarFiltros = () => {
    const vacios: Filtros = { fecha_inicio: '', fecha_fin: '', modulo: '', accion: '', usuario_id: '' };
    setFiltros(vacios);
    setPagina(1);
    cargar(1, vacios);
  };

  // ---------------------------------------------------------------------------
  // Exportar CSV
  // ---------------------------------------------------------------------------
  const exportarCSV = async () => {
    const token = localStorage.getItem(STORAGE_TOKEN_KEY);
    const params = new URLSearchParams();
    if (filtros.fecha_inicio) params.append('fecha_inicio', filtros.fecha_inicio);
    if (filtros.fecha_fin) params.append('fecha_fin', filtros.fecha_fin);
    if (filtros.modulo) params.append('modulo', filtros.modulo);
    if (filtros.accion) params.append('accion', filtros.accion);

    const url = `${API_BASE_URL}/auditoria/export?${params}`;
    const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!resp.ok) { alert('Error al exportar'); return; }
    const blob = await resp.blob();
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'auditoria.csv';
    link.click();
  };

  // ---------------------------------------------------------------------------
  // Paginación
  // ---------------------------------------------------------------------------
  const totalPaginas = Math.max(1, Math.ceil(total / POR_PAGINA));

  return (
    <div className="p-4 space-y-4 max-w-7xl mx-auto">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-xl font-bold text-gray-900">Auditoría del Sistema</h1>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => cargar(pagina, filtros)} disabled={cargando}>
            <RefreshCw size={14} className={cargando ? 'animate-spin' : ''} />
            <span className="ml-1 hidden sm:inline">Actualizar</span>
          </Button>
          <Button variant="outline" size="sm" onClick={exportarCSV}>
            <Download size={14} />
            <span className="ml-1 hidden sm:inline">Exportar CSV</span>
          </Button>
        </div>
      </div>

      {/* Filtros */}
      <Card className="p-4">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Desde</label>
            <Input
              type="date"
              value={filtros.fecha_inicio}
              onChange={e => setFiltros(f => ({ ...f, fecha_inicio: e.target.value }))}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Hasta</label>
            <Input
              type="date"
              value={filtros.fecha_fin}
              onChange={e => setFiltros(f => ({ ...f, fecha_fin: e.target.value }))}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Módulo</label>
            <select
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={filtros.modulo}
              onChange={e => setFiltros(f => ({ ...f, modulo: e.target.value }))}
            >
              <option value="">Todos</option>
              {Object.keys(ETIQUETAS_MODULO).map(m => (
                <option key={m} value={m}>{ETIQUETAS_MODULO[m]}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Acción</label>
            <select
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={filtros.accion}
              onChange={e => setFiltros(f => ({ ...f, accion: e.target.value }))}
            >
              <option value="">Todas</option>
              {Object.keys(ETIQUETAS_ACCION).map(a => (
                <option key={a} value={a}>{ETIQUETAS_ACCION[a].label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Usuario ID</label>
            <Input
              placeholder="ID usuario..."
              value={filtros.usuario_id}
              onChange={e => setFiltros(f => ({ ...f, usuario_id: e.target.value }))}
            />
          </div>
        </div>
        <div className="flex gap-2 mt-3">
          <Button size="sm" onClick={aplicarFiltros} disabled={cargando}>
            <Search size={14} className="mr-1" />
            Filtrar
          </Button>
          <Button variant="outline" size="sm" onClick={limpiarFiltros}>
            Limpiar
          </Button>
        </div>
      </Card>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">{error}</div>
      )}

      {/* Tabla */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Fecha</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Usuario</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Rol</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Acción</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Módulo</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Descripción</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wide">Detalle</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {cargando && (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-gray-400">
                    <RefreshCw size={20} className="animate-spin inline mr-2" />
                    Cargando...
                  </td>
                </tr>
              )}
              {!cargando && registros.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-gray-400">
                    Sin registros para los filtros seleccionados
                  </td>
                </tr>
              )}
              {!cargando && registros.map(log => (
                <tr key={log.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 text-gray-600 whitespace-nowrap text-xs">{formatFecha(log.created_at)}</td>
                  <td className="px-4 py-3 text-gray-800 text-xs">
                    <div className="font-medium">{log.usuario_nombre || log.usuario_id}</div>
                    <div className="text-[11px] text-gray-500">{log.usuario_username || log.usuario_id}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-xs text-gray-700">{log.rol || log.rol_usuario}</div>
                    <div className="text-[11px] text-gray-500">{log.local_nombre || 'Sin local'}</div>
                  </td>
                  <td className="px-4 py-3">
                    <AccionBadge accion={log.accion} />
                  </td>
                  <td className="px-4 py-3 text-gray-700 text-xs">
                    {ETIQUETAS_MODULO[log.modulo] ?? log.modulo}
                  </td>
                  <td className="px-4 py-3 text-gray-600 text-xs max-w-xs truncate" title={log.descripcion}>
                    {log.descripcion}
                  </td>
                  <td className="px-4 py-3 text-center" title={`ID: ${log.usuario_id}${log.ip ? ` | IP: ${log.ip}` : ''}`}>
                    {(log.datos_antes || log.datos_despues) ? (
                      <button
                        onClick={() => setLogSeleccionado(log)}
                        className="text-blue-600 hover:text-blue-800 p-1 rounded"
                        title="Ver detalle"
                      >
                        <Eye size={16} />
                      </button>
                    ) : (
                      <span className="text-gray-300">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Paginación */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
          <span className="text-xs text-gray-500">
            {total} registro{total !== 1 ? 's' : ''} encontrado{total !== 1 ? 's' : ''}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPagina(p => Math.max(1, p - 1))}
              disabled={pagina <= 1}
              className="p-1 rounded text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronLeft size={16} />
            </button>
            <span className="text-xs text-gray-600">
              Página {pagina} de {totalPaginas}
            </span>
            <button
              onClick={() => setPagina(p => Math.min(totalPaginas, p + 1))}
              disabled={pagina >= totalPaginas}
              className="p-1 rounded text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </Card>

      {/* Modal detalle */}
      {logSeleccionado && (
        <Modal
          isOpen={true}
          onClose={() => setLogSeleccionado(null)}
          title={`Detalle — Log #${logSeleccionado.id}`}
        >
          <div className="space-y-4 text-sm">
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div><span className="font-medium text-gray-500">Fecha:</span> {formatFecha(logSeleccionado.created_at)}</div>
              <div><span className="font-medium text-gray-500">Módulo:</span> {ETIQUETAS_MODULO[logSeleccionado.modulo] ?? logSeleccionado.modulo}</div>
              <div><span className="font-medium text-gray-500">Acción:</span> <AccionBadge accion={logSeleccionado.accion} /></div>
              <div><span className="font-medium text-gray-500">Entidad ID:</span> {logSeleccionado.entidad_id ?? '—'}</div>
              <div><span className="font-medium text-gray-500">Usuario ID:</span> {logSeleccionado.usuario_id}</div>
              <div><span className="font-medium text-gray-500">Usuario:</span> {logSeleccionado.usuario_nombre || '—'} ({logSeleccionado.usuario_username || logSeleccionado.usuario_id})</div>
              <div><span className="font-medium text-gray-500">Rol:</span> {logSeleccionado.rol || logSeleccionado.rol_usuario}</div>
              <div><span className="font-medium text-gray-500">Local:</span> {logSeleccionado.local_nombre || '—'}</div>
              {logSeleccionado.ip && <div><span className="font-medium text-gray-500">IP:</span> {logSeleccionado.ip}</div>}
            </div>
            <div className="border-t pt-3">
              <p className="text-xs font-semibold text-gray-700 mb-1">Descripción</p>
              <p className="text-gray-600 text-sm">{logSeleccionado.descripcion}</p>
            </div>
            {(logSeleccionado.datos_antes || logSeleccionado.datos_despues) && (
              <div className="border-t pt-3 space-y-3">
                <JsonViewer titulo="Datos ANTES" json={logSeleccionado.datos_antes} />
                {(() => {
                  const ticketLegible = extraerTicketLegible(logSeleccionado);
                  if (ticketLegible) {
                    return (
                      <div>
                        <p className="text-xs font-semibold text-gray-500 mb-1">Datos DESPUÉS</p>
                        <TicketDetalleNegocio data={ticketLegible} />
                      </div>
                    );
                  }
                  return <JsonViewer titulo="Datos DESPUÉS" json={logSeleccionado.datos_despues} />;
                })()}
              </div>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
};

export default AuditoriaPage;
